import {interpolateArray} from "../../../charts/StockChart/interpolate";
import {isUUID} from "../../../utils/strings";
import {COLD_PALETTE} from "../../../utils/colorUtils";
import {getDeltaDaysFromTimestamps} from "../../../utils/datetime";
import {filterSeriesBetweenRange, rewindowSeriesToMonthly} from "../../../utils/dataTransforms";
import {formatDataForPieGrid} from "../../../SuperComponents/DataDisplay/PieGrid/pieGridUtils";

export const formatDataForStockChart = (portfolioTimeSeries, benchmarkTimeSeries, extraTimeseries, snapshotCallback, portfolio)=>{
    const dataSeries = [];
    if (portfolioTimeSeries) {
        const closeValues = interpolateArray(portfolioTimeSeries.map(x => x.close));
        dataSeries.push({
            name: portfolio?.name,
            type: 'line',
            data: portfolioTimeSeries.map(x => [x.timestamp, closeValues[portfolioTimeSeries.indexOf(x)], x.snapshot]),
            stroke: '#105DEB',
            thickness: 2,
            hasSnapshot: true,
            callbackOnSnapshot: snapshotCallback
        });
    }

    if (benchmarkTimeSeries){
        dataSeries.push({
            name: portfolio?.benchmark,
            type: 'area',
            data: benchmarkTimeSeries.map(x => [x.timestamp, x['adj close']]),
            stroke: 'rgba(160, 184, 202, 1)',
            fill: {
                keys: ['rgba(160, 184, 202, 1)', 'rgba(160, 184, 202, 0.3)', 'rgba(160, 184, 202, 0.06)'],
                angle: 270
            }
        });
    }
    if (extraTimeseries){
        extraTimeseries.forEach((extraSeries, index)=>{
            if (extraSeries.symbol !== portfolio?.benchmark && !isUUID(extraSeries.symbol))
            dataSeries.push({
                name: extraSeries.symbol,
                type: 'line',
                data: extraSeries.data.map(x => [x.timestamp, x['adj close']]),
                stroke: COLD_PALETTE[index < COLD_PALETTE.length ? index : index % COLD_PALETTE.length],
                strokeWidth: 2,
            });
        })
    }
    return dataSeries;
}



/**
 * Handles the range change for the chart and updates the pie data accordingly.
 *
 * @param {Date} start - The start date of the range.
 * @param {Date} end - The end date of the range.
 * @param {Array} portfolioTimeSeries - The portfolio time series data.
 * @param {Function} updatePieData - Function to update the pie data.
 * @param {Function} togglePies - Function to toggle pie chart visibility.
 */
export const handleRangeChange = (start, end, portfolioTimeSeries, updatePieData, togglePies) => {
    const days = getDeltaDaysFromTimestamps(start, end);
    if (days < 390) {
        const rewindowedSeries = rewindowSeriesToMonthly([...portfolioTimeSeries]);
        const filteredSeries = filterSeriesBetweenRange(rewindowedSeries, new Date(start), new Date(end));

        const unwindSnapshot = (snapshot) => {
            return Object.fromEntries(Object.entries(snapshot).map(([key, value]) => [key, value.wgt]));
        };

        const newData = formatDataForPieGrid({
            data: filteredSeries.map(x => ({
                timestamp: x.timestamp,
                ...unwindSnapshot(x.snapshot)
            })),
            idField: 'timestamp'
        });

        updatePieData(newData);
    } else {
        togglePies(false);  // Hide pies if the condition is not met
    }
};

const _transformSnapshot = (snapshot) => {
    const items = [];
    for (let symbol in snapshot) {
        items.push({symbol, weight: snapshot[symbol].wgt});
    }
    return items;
}


/**
 * Calculate the delta (change) between snapshots and return a new array of objects
 * with the 'timestamp' and 'value' fields.
 *
 * @param {Array} series - Array of objects containing 'timestamp' and 'snapshot' fields.
 *                         'snapshot' should be an array of objects with 'symbol' and 'weight' fields.
 * @returns {Array} - Array of arrays with each element in the format [timestamp, value],
 *                    where 'value' is the sum of absolute changes in weights between consecutive snapshots.
 */
export const getDeltaSeries = (series)=> {
    let deltaSeries = [];
    console.log(series);
    for (let i = 1; i < series.length; i++) {
        const previousSnapshot = _transformSnapshot(series[i - 1].snapshot);
        const currentSnapshot = _transformSnapshot(series[i].snapshot);
        const timestamp = series[i].timestamp;

        // Create a map of symbols to their weights for easier lookup
        const previousWeights = new Map(previousSnapshot.map(item => [item.symbol, item.weight]));
        const currentWeights = new Map(currentSnapshot.map(item => [item.symbol, item.weight]));

        // Calculate the total absolute change in weights
        let totalChange = 0;

        // Iterate through all symbols in the current snapshot
        for (let [symbol, currentWeight] of currentWeights) {
            const previousWeight = previousWeights.get(symbol) || 0;
            totalChange += Math.abs(currentWeight - previousWeight);
        }

        // Also consider symbols that were in the previous snapshot but not in the current one
        for (let [symbol, previousWeight] of previousWeights) {
            if (!currentWeights.has(symbol)) {
                totalChange += Math.abs(previousWeight);
            }
        }
        if (totalChange>0) {
            totalChange *= 100;
        }

        deltaSeries.push([timestamp, totalChange]);
    }

    return deltaSeries;
}
